import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { HandWaving, Compass, Lightning, Coffee, Crown, Heart, ArrowLeft, ArrowUpRight, LockSimple } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
const ROOM_ICONS = { welcome: HandWaving, explorer: Compass, genz: Lightning, millennials: Coffee, vip: Crown };

const PresenceList = ({ people, prefix }) => (
  <div data-testid={`${prefix}-presence`}>
    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mb-3">
      <span className="flex items-center gap-1"><Heart weight="fill" className="text-green-400" /> {people.filter(p => p.state === 'active').length} active</span>
      <span className="flex items-center gap-1"><Heart weight="fill" className="text-yellow-400" /> {people.filter(p => p.state === 'passive').length} passive</span>
    </div>
    <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
      {people.length ? people.map(person => (
        <Link to={`/u/${encodeURIComponent(person.username)}`} key={person.user_id} data-testid={`${prefix}-participant-${person.user_id}`} className="inline-flex items-center gap-1.5 px-2 py-1 bg-white/5 text-xs hover:bg-white/10 transition-colors max-w-full" title={`${person.username} · ${person.state}`}>
          <Heart weight="fill" className={`shrink-0 ${person.state === 'active' ? 'text-green-400' : 'text-yellow-400'}`} />
          <span className="truncate">{person.username}</span><span className="text-muted-foreground">· {person.state}</span>
        </Link>
      )) : <p className="text-xs text-muted-foreground" data-testid={`${prefix}-empty`}>Quiet for now. Be the first to say hello.</p>}
    </div>
  </div>
);

const RoomDetails = ({ room, prefix }) => (
  <>
    <p className="text-sm text-muted-foreground leading-relaxed" data-testid={`${prefix}-description`}>{room.description}</p>
    <p className="text-xs text-muted-foreground mt-3" data-testid={`${prefix}-creator`}>Created by <span className="text-foreground">{room.created_by}</span></p>
    <div className="py-5 my-5 border-y border-white/10" data-testid={`${prefix}-rules`}>
      <p className="text-xs uppercase tracking-[0.18em] mb-3">Room rules</p>
      <ul className="space-y-2 text-xs leading-relaxed text-muted-foreground list-disc pl-4">{room.rules.map(rule => <li key={rule}>{rule}</li>)}</ul>
    </div>
    {room.locked ? <p className="text-xs text-amber-300" data-testid={`${prefix}-locked`}><LockSimple className="inline mr-1" /> Members and conversation are visible to Elite members only.</p> : <PresenceList people={room.participants} prefix={prefix} />}
  </>
);

const RoomCard = ({ room, onJoin }) => {
  const Icon = ROOM_ICONS[room.icon] || HandWaving;
  return (
    <Card className={`relative p-6 sm:p-8 flex flex-col transition-colors hover:border-white/30 ${room.elite_only ? 'lg:col-span-2 border-amber-400/30 bg-amber-400/[0.03]' : ''}`} data-testid={`room-card-${room.id}`}>
      <div className="flex justify-between items-start gap-4 mb-4">
        <div><p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">{room.elite_only ? 'The Elite lounge' : 'A place to connect'}</p><h2 className="text-3xl sm:text-4xl font-light" data-testid={`room-title-${room.id}`}>{room.name}</h2></div>
        <Icon size={48} weight="duotone" className={room.elite_only ? 'text-amber-300 shrink-0' : 'text-primary shrink-0'} />
      </div>
      <RoomDetails room={room} prefix={`room-${room.id}`} />
      <div className="pt-6 mt-auto">
        {room.locked ? <Button asChild variant="outline" className="rounded-full border-amber-400/40 text-amber-300"><Link to="/membership" data-testid="vip-upgrade-link"><Crown className="mr-2" /> Unlock with Elite</Link></Button> : <Button onClick={() => onJoin(room.id)} className="rounded-full" data-testid={`room-button-${room.id}`}>Join {room.name}<ArrowUpRight className="ml-2" /></Button>}
      </div>
    </Card>
  );
};

export function ChatRoomsPage() {
  const { user, setUser } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [roomError, setRoomError] = useState('');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [retry, setRetry] = useState(0);
  const wsRef = useRef(null);
  const messagesRef = useRef(null);
  const room = rooms.find(item => item.id === selectedRoom);

  const loadRooms = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chat/rooms`, { withCredentials: true });
      setRooms(data);
      setRoomError('');
    } catch {
      setRoomError('Could not refresh rooms. Please try again.');
    }
  }, []);

  useEffect(() => {
    loadRooms();
    const interval = setInterval(loadRooms, 15000);
    return () => clearInterval(interval);
  }, [loadRooms]);

  useEffect(() => {
    setConnected(false);
    setConnectionError('');
    setMessages([]);
    setMessageInput('');
    setConnecting(Boolean(selectedRoom));
    if (!selectedRoom) return;
    let disposed = false;
    let socket;
    const controller = new AbortController();
    let lastActivity = Date.now();
    let lastHeartbeat = 0;
    const sendPresence = () => {
      if (socket?.readyState === WebSocket.OPEN) {
        lastHeartbeat = Date.now();
        socket.send(JSON.stringify({ type: 'presence', active: !document.hidden && Date.now() - lastActivity < 60000 }));
      }
    };
    const onActivity = () => {
      lastActivity = Date.now();
      if (Date.now() - lastHeartbeat > 2000) sendPresence();
    };
    const onVisibility = () => {
      if (!document.hidden) lastActivity = Date.now();
      sendPresence();
    };
    const connect = async () => {
      try {
        const { data } = await axios.post(`${API}/chat/rooms/${selectedRoom}/ticket`, {}, {
          withCredentials: true,
          headers: { 'X-Chat-Connection': '1' },
          signal: controller.signal,
          timeout: 15000,
        });
        if (disposed) return;
        socket = new WebSocket(`${WS_URL}/api/ws/chat/${selectedRoom}?ticket=${encodeURIComponent(data.ticket)}`);
        wsRef.current = socket;
        socket.onopen = () => { if (!disposed) { setConnecting(false); setConnected(true); sendPresence(); } };
        socket.onmessage = event => {
          if (disposed) return;
          const data = JSON.parse(event.data);
          if (data.type === 'message') setMessages(previous => [...previous, data]);
          if (data.type === 'presence') setRooms(previous => previous.map(item => item.id === selectedRoom ? { ...item, participants: data.participants } : item));
          if (data.type === 'credits') setUser(previous => ({ ...previous, chat_credits: data.chat_credits }));
          if (data.type === 'error') toast.error(data.message);
        };
        socket.onclose = event => {
          if (!disposed) { setConnecting(false); setConnected(false); setConnectionError(event.reason || 'Connection ended. Rejoin to continue.'); }
        };
        socket.onerror = () => { if (!disposed) setConnectionError('Could not connect to this room. Check your access and try again.'); };
      } catch (error) {
        if (disposed) return;
        setConnecting(false);
        setConnectionError(error.response?.status === 401
          ? 'Your session has expired. Please sign in again to chat.'
          : typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Could not connect. Please rejoin the room.');
      }
    };
    connect();
    const heartbeat = setInterval(sendPresence, 15000);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pointerdown', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('pointermove', onActivity);
    return () => {
      disposed = true;
      clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('pointermove', onActivity);
      controller.abort();
      socket?.close();
      wsRef.current = null;
    };
  }, [selectedRoom, retry, setUser]);

  useEffect(() => {
    if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = () => {
    if (!messageInput.trim() || wsRef.current?.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ message: messageInput.trim() }));
    setMessageInput('');
  };

  return (
    <div className="min-h-screen py-8 md:py-12 px-4 sm:px-6" data-testid="chatrooms-page">
      <div className="w-full max-w-7xl mx-auto">
        <div className="mb-8 md:mb-12 flex flex-wrap justify-between items-end gap-4">
          <div><p className="text-xs uppercase tracking-[0.25em] text-primary mb-3">Find your people</p><h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight" data-testid="chatrooms-heading">Chat Rooms</h1><p className="text-sm text-muted-foreground mt-3">Five different spaces. The same freedom to be yourself.</p></div>
          <p className="text-xs text-muted-foreground" data-testid="presence-legend"><Heart weight="fill" className="text-green-400 inline" /> Active · <Heart weight="fill" className="text-yellow-400 inline" /> Passive after 1 minute idle or in a background tab</p>
        </div>
        {roomError && <div role="alert" data-testid="rooms-error" className="p-4 border border-red-400/30 mb-4 text-sm">{roomError}<Button variant="ghost" onClick={loadRooms} data-testid="rooms-retry-button">Retry</Button></div>}
        {!rooms.length && !roomError && <p data-testid="rooms-loading" role="status" className="text-muted-foreground">Opening the doors…</p>}
        {!selectedRoom ? <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="room-list">{rooms.map(item => <RoomCard key={item.id} room={item} onJoin={setSelectedRoom} />)}</div> : room && (
          <>
            <Button variant="ghost" className="mb-5 rounded-full" onClick={() => { setSelectedRoom(null); loadRooms(); }} data-testid="leave-room-button"><ArrowLeft className="mr-2" /> All rooms</Button>
            <Card className="overflow-hidden" data-testid="chat-container">
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <aside className="lg:col-span-4 p-6 sm:p-8 bg-secondary/30 border-b lg:border-b-0 lg:border-r border-white/10">
                  <div className="flex gap-3 items-center mb-5">{(() => { const Icon = ROOM_ICONS[room.icon]; return <Icon size={40} weight="duotone" className={room.elite_only ? 'text-amber-300' : 'text-primary'} />; })()}<h2 className="text-3xl font-light" data-testid="active-room-title">{room.name}</h2></div>
                  <RoomDetails room={room} prefix="active-room" />
                </aside>
                <section className="lg:col-span-8 min-w-0 p-4 sm:p-8">
                  <div className="flex items-center justify-between gap-3 mb-5 text-xs">
                    <span className={connected ? 'text-green-400' : 'text-yellow-400'} data-testid="connection-status">{connected ? 'Connected' : connecting ? 'Connecting…' : 'Disconnected'}</span>
                    {user?.membership_level === 0 && <span className="text-muted-foreground">Credits: <span className="text-primary" data-testid="chat-credits-remaining">{user.chat_credits}</span></span>}
                  </div>
                  {connectionError && <div role="alert" className="p-3 mb-4 border border-yellow-400/30 text-xs" data-testid="chat-connection-error">{connectionError}<Button variant="ghost" size="sm" onClick={() => setRetry(value => value + 1)} data-testid="chat-reconnect-button">Rejoin</Button></div>}
                  <div ref={messagesRef} className="h-[340px] sm:h-[440px] overflow-y-auto mb-5 space-y-4" data-testid="messages-container" role="log" aria-label="Chat messages">
                    {messages.length === 0 ? <div className="pt-16 text-center text-muted-foreground" data-testid="no-messages"><HandWaving size={36} weight="duotone" className="mx-auto mb-4 text-primary" /><p className="text-sm">A good conversation starts with hello.</p><p className="text-xs mt-2">Messages sent while you are here will appear below.</p></div> : messages.map((msg, index) => (
                      <div key={index} className="pb-4 border-b border-white/5" data-testid={`message-${index}`}><div className="flex items-baseline gap-2"><Link to={`/u/${encodeURIComponent(msg.username)}`} className="text-sm font-medium text-primary hover:underline" data-testid={`message-author-${index}`}>{msg.username}</Link><span className="text-[10px] text-muted-foreground">{new Date(msg.timestamp).toLocaleTimeString()}</span></div><p className="text-sm mt-1 whitespace-pre-wrap break-words">{msg.message}</p></div>
                    ))}
                  </div>
                  <form className="flex gap-2" onSubmit={event => { event.preventDefault(); sendMessage(); }}>
                    <Input aria-label="Chat message" placeholder="Say something kind…" maxLength={2000} value={messageInput} onChange={event => setMessageInput(event.target.value)} disabled={!connected || room.locked} data-testid="message-input" />
                    <Button type="submit" disabled={!connected || room.locked || !messageInput.trim()} className="rounded-full" data-testid="send-message-button">Send</Button>
                  </form>
                  {user?.membership_level === 0 && user.chat_credits < 10 && <div className="mt-4 p-4 bg-primary/10 text-sm" data-testid="low-credits-warning">Running low on chat credits? <Link to="/membership" className="text-primary underline" data-testid="upgrade-membership-button">Go Elite for unlimited chatting</Link></div>}
                </section>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}